/* Bella Ballou: gaveæske på produktside, i kurv-skuffe og på kurvside (snippets/bb-cart-gift.liquid) */
if (!customElements.get('bb-cart-gift')) {
  class BbCartGift extends HTMLElement {
    static strings = {
      addError: 'Gaveæsken kunne ikke tilføjes. Prøv igen.',
      removeError: 'Gaveæsken kunne ikke fjernes. Prøv igen.',
      soldOut: 'Gaveæsken er desværre ikke på lager lige nu.',
      messageError: 'Gavebeskeden kunne ikke gemmes. Prøv igen.',
    };

    static orphanRemoval = false;

    static route(name, fallback) {
      const base = (window.routes && window.routes[name]) || fallback;
      return base.endsWith('.js') ? base : `${base}.js`;
    }

    static fetchCart() {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      return fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => {
        if (!response.ok) throw new Error(`cart.js ${response.status}`);
        return response.json();
      });
    }

    get isProductContext() {
      return this.dataset.context === 'product';
    }

    connectedCallback() {
      this.variantId = parseInt(this.dataset.variantId, 10);
      this.checkbox = this.querySelector('.bb-cart-gift__checkbox');
      this.errorElement = this.querySelector('.bb-cart-gift__error');
      this.messageWrapper = this.querySelector('.bb-cart-gift__message');
      this.textarea = this.querySelector('.bb-cart-gift__textarea');

      // Produktside: kun et valg, der sendes med "Læg i kurv". Kurv: ændrer kurven med det samme.
      // Produktside: valget sendes med "Læg i kurv". Ligger produktet allerede i kurven (tilvalget står under
      // beskrivelsen og ses ofte efter tilføjelsen), ændres kurven med det samme. Kurv: ændrer altid med det samme.
      this.onCheckboxChange = () => {
        if (!this.isProductContext) return this.toggle(this.checkbox.checked);
        this.showError('');
        this.syncProductChoice();
      };
      this.checkbox?.addEventListener('change', this.onCheckboxChange);

      if (this.textarea) {
        this.onMessageInput = debounce(() => this.saveMessage(), 600);
        this.textarea.addEventListener('input', this.onMessageInput);
      }

      // Liquid renderer skjult med data-orphan, når kurven kun rummer gaveæsken: ryd op med det samme.
      if (!this.isProductContext && this.dataset.orphan !== undefined) this.sync();

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.bbCartGiftOrigin === this) return;
          if (this.isProductContext) return this.onProductAdded(event);
          this.sync();
        });
      }
    }

    disconnectedCallback() {
      this.checkbox?.removeEventListener('change', this.onCheckboxChange);
      this.textarea?.removeEventListener('input', this.onMessageInput);
      if (this.unsubscribe) this.unsubscribe();
      this.unsubscribe = null;
    }

    // Kurv: afspejl kurvens aktuelle tilstand (fx efter at en linje er fjernet et andet sted).
    sync() {
      if (this.busy) return;
      BbCartGift.fetchCart()
        .then((cart) => {
          if (this.isConnected && !this.busy) this.applyCart(cart);
        })
        .catch((error) => console.error(error));
    }

    applyCart(cart) {
      const giftLines = cart.items.filter((item) => item.variant_id === this.variantId);
      // Gaveæsken må ikke stå alene: uden mindst én anden linje skjules tilvalget (kun kurv, ikke produktsiden).
      const hasRealItems = cart.items.length > giftLines.length;
      this.setChecked(giftLines.length > 0);
      if (this.isProductContext) {
        this.hidden = cart.item_count === 0;
        return;
      }
      this.hidden = !hasRealItems;
      if (!hasRealItems && giftLines.length > 0) this.removeOrphanGift(cart, giftLines);
    }

    // Fjerner en gaveæske, der ligger alene i kurven. Én fælles removal ad gangen på tværs af instanser (skuffe + kurvside).
    // Kører kun, mens gaveæsken stadig er i kurven; egne cartUpdate-events ignoreres (bbCartGiftOrigin), så ingen løkke.
    async removeOrphanGift(cart, giftLines) {
      if (BbCartGift.orphanRemoval || !this.variantId) return;
      BbCartGift.orphanRemoval = true;
      const wasBusy = this.busy;
      this.setBusy(true);
      try {
        await this.removeGift(cart, giftLines);
        if (this.isConnected) this.setChecked(false);
      } catch (error) {
        console.error(error);
        // Fjernelsen fejlede: vis tilvalget igen, så kunden selv kan fjerne gaveæsken.
        if (this.isConnected) {
          this.hidden = false;
          this.showError(BbCartGift.strings.removeError);
        }
      } finally {
        BbCartGift.orphanRemoval = false;
        if (!wasBusy) this.setBusy(false);
      }
    }

    setChecked(checked) {
      if (this.checkbox) this.checkbox.checked = checked;
      if (this.messageWrapper) this.messageWrapper.hidden = !checked;
    }

    setBusy(busy) {
      this.busy = busy;
      if (this.checkbox) this.checkbox.disabled = busy;
      this.classList.toggle('bb-cart-gift--loading', busy);
      this.setAttribute('aria-busy', String(busy));
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.textContent = message || '';
      this.errorElement.hidden = !message;
    }

    // Produktside: Dawns product-form har lagt varen i kurven (og gengivet skuffen). Tilføj gaveæsken bagefter.
    async onProductAdded(event) {
      if (!event || event.source !== 'product-form' || !this.checkbox?.checked || this.busy) return;
      const form = document.getElementById(this.dataset.formId);
      const formVariantId = form?.querySelector('[name="id"]')?.value;
      if (!formVariantId || String(event.productVariantId) !== String(formVariantId)) return;
      if (String(event.productVariantId) === String(this.variantId)) return;

      this.setBusy(true);
      this.showError('');
      try {
        const cart = await BbCartGift.fetchCart();
        if (!cart.items.some((item) => item.variant_id === this.variantId)) await this.addGift(cart);
      } catch (error) {
        console.error(error);
        this.showError(error.status === 422 ? BbCartGift.strings.soldOut : BbCartGift.strings.addError);
      } finally {
        this.setBusy(false);
      }
    }

    async syncProductChoice() {
      if (this.busy) return;
      const form = document.getElementById(this.dataset.formId);
      const formVariantId = form?.querySelector('[name="id"]')?.value;
      if (!formVariantId) return;
      try {
        const cart = await BbCartGift.fetchCart();
        if (cart.items.some((item) => String(item.variant_id) === String(formVariantId))) this.toggle(this.checkbox.checked);
      } catch (error) {
        console.error(error);
      }
    }

    // Kurv: afkrydsning tilføjer 1 gaveæske, fjernelse af markering fjerner gaveæske-linjen.
    async toggle(checked) {
      if (this.busy || !this.variantId) return;
      this.setBusy(true);
      this.showError('');

      try {
        // Læs kurven frisk, så vi aldrig tilføjer en ekstra gaveæske eller fjerner noget, der ikke findes.
        const cart = await BbCartGift.fetchCart();
        const lines = cart.items.filter((item) => item.variant_id === this.variantId);

        if (checked === lines.length > 0) {
          this.applyCart(cart);
        } else if (checked) {
          await this.addGift(cart);
          if (this.isConnected) this.setChecked(true);
        } else {
          await this.removeGift(cart, lines);
          if (this.isConnected) this.setChecked(false);
        }
      } catch (error) {
        console.error(error);
        this.setChecked(!checked);
        const key = checked ? 'addError' : 'removeError';
        this.showError(error.status === 422 && checked ? BbCartGift.strings.soldOut : BbCartGift.strings[key]);
      } finally {
        this.setBusy(false);
      }
    }

    async addGift(cart) {
      const sections = this.getSectionsToRender();
      // Linjeegenskab med tørklædets navn (samme nøgle som Wrapin brugte), så lageret ved, hvilken størrelse der skal sendes.
      const titles = [...new Set(cart.items.map((item) => item.product_title))].join(', ');
      const state = await this.post(BbCartGift.route('cart_add_url', '/cart/add'), {
        items: [{ id: this.variantId, quantity: 1, properties: titles ? { 'Product Name': titles } : {} }],
        ...this.sectionParams(sections),
      });
      this.renderSections(state, sections, cart.item_count + 1);
      this.publishUpdate(state);
    }

    async removeGift(cart, lines) {
      const sections = this.getSectionsToRender();
      let state = await this.post(BbCartGift.route('cart_change_url', '/cart/change'), {
        id: lines[0].key,
        quantity: 0,
        ...this.sectionParams(sections),
      });

      // Flere gaveæske-linjer (fx én pr. tørklæde): fjern resten og en evt. gavebesked i samme omgang.
      const extra = lines.slice(1);
      const clearMessage = Boolean(cart.attributes && cart.attributes.Gavebesked);
      if (extra.length || clearMessage) {
        const updates = {};
        extra.forEach((line) => {
          updates[line.key] = 0;
        });
        state = await this.post(BbCartGift.route('cart_update_url', '/cart/update'), {
          updates,
          ...(clearMessage ? { attributes: { Gavebesked: '' } } : {}),
          ...this.sectionParams(sections),
        });
      }

      this.renderSections(state, sections, state.item_count);
      this.publishUpdate(state);
    }

    publishUpdate(state) {
      // source 'cart-items': sektionerne er allerede gengivet her, så CartItems.onCartUpdate ikke henter dem igen.
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: state,
        variantId: this.variantId,
        bbCartGiftOrigin: this,
      });
    }

    async post(url, body) {
      const response = await fetch(url, { ...fetchConfig(), body: JSON.stringify(body) });
      const json = await response.json().catch(() => ({}));
      if (!response.ok || json.status >= 400 || json.errors) {
        const error = new Error(json.description || json.message || `${url} ${response.status}`);
        error.status = response.status;
        throw error;
      }
      return json;
    }

    sectionParams(sections) {
      return { sections: sections.map((section) => section.section), sections_url: window.location.pathname };
    }

    // Samme sektioner som Dawns cart.js / cart-drawer.js (skuffe, kurvside, kurv-ikon).
    getSectionsToRender() {
      const sections = [];
      const drawerItems = document.querySelector('cart-drawer-items');
      if (drawerItems && typeof drawerItems.getSectionsToRender === 'function' && document.getElementById('CartDrawer')) {
        sections.push(...drawerItems.getSectionsToRender());
      }
      const pageItems = document.querySelector('cart-items');
      if (
        pageItems &&
        typeof pageItems.getSectionsToRender === 'function' &&
        document.getElementById('main-cart-items') &&
        document.getElementById('main-cart-footer')
      ) {
        sections.push(...pageItems.getSectionsToRender());
      }
      if (!sections.length) sections.push({ id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' });

      const seen = new Set();
      return sections.filter((section) => !seen.has(section.section) && seen.add(section.section)).slice(0, 5);
    }

    renderSections(state, sections, itemCount) {
      const isEmpty = itemCount === 0;
      document.querySelector('cart-drawer')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-drawer-items')?.classList.toggle('is-empty', isEmpty);
      document.querySelector('cart-items')?.classList.toggle('is-empty', isEmpty);
      document.getElementById('main-cart-footer')?.classList.toggle('is-empty', isEmpty);

      if (!state.sections) return;
      const focusId = this.checkbox && this.contains(document.activeElement) ? this.checkbox.id : null;

      sections.forEach(({ id, section, selector }) => {
        const html = state.sections[section];
        const host = document.getElementById(id);
        if (!html || !host) return;
        const target = host.querySelector(selector) || host;
        const source = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
        if (source) target.innerHTML = source.innerHTML;
      });

      if (focusId && !this.isConnected) document.getElementById(focusId)?.focus();
    }

    saveMessage() {
      if (!this.textarea) return;
      this.showError('');
      fetch(BbCartGift.route('cart_update_url', '/cart/update'), {
        ...fetchConfig(),
        body: JSON.stringify({ attributes: { Gavebesked: this.textarea.value } }),
      })
        .then((response) => {
          if (!response.ok) throw new Error(`cart/update.js ${response.status}`);
        })
        .catch((error) => {
          console.error(error);
          this.showError(BbCartGift.strings.messageError);
        });
    }
  }

  customElements.define('bb-cart-gift', BbCartGift);
}
